/**
 * 统一 API 错误对象，便于拦截器与页面处理
 */
export class ApiError extends Error {
  constructor(message, code = -1, httpStatus, raw) {
    super(message || '请求失败')
    this.name = 'ApiError'
    this.code = code
    this.httpStatus = httpStatus
    this.raw = raw
  }
}

const DEFAULT_MESSAGES = {
  400: '请求参数有误',
  401: '登录已过期，请重新登录',
  403: '无权限执行此操作',
  404: '请求的资源不存在',
  500: '系统繁忙，请稍后重试',
  network: '网络异常，请检查连接后重试',
  timeout: '请求超时，请稍后重试'
}

const BIZ_MESSAGE_REG = /BizException:\s*([^\r\n]+)/

/**
 * 清洗错误文案，绝不向用户展示 Java 堆栈
 */
export function sanitizeErrorMessage(message) {
  if (!message) return ''

  const bizMatch = message.match(BIZ_MESSAGE_REG)
  if (bizMatch) {
    return bizMatch[1].trim()
  }

  const firstLine = message.split(/\r?\n/)[0].trim()

  if (looksLikeStackTrace(message)) {
    const inline = firstLine.match(/(?:Exception|Error):\s*(.+)$/)
    if (inline && inline[1] && !looksLikeStackTrace(inline[1])) {
      return inline[1].trim()
    }
    return ''
  }

  if (firstLine.includes('Exception:') || firstLine.includes('Error:')) {
    const idx = firstLine.indexOf(':')
    const tail = firstLine.substring(idx + 1).trim()
    if (tail && !looksLikeStackTrace(tail)) {
      return tail
    }
  }

  return firstLine
}

function looksLikeStackTrace(text) {
  return /at\s+[\w.$]+\(/i.test(text)
    || /\.java:\d+/.test(text)
    || /ExceptionFilter|invokeMethod|dubbo/i.test(text)
}

export function resolveErrorTitle(code, httpStatus, message = '') {
  if (code === 401 || httpStatus === 401) {
    if (/密码|用户名|账号|登录/.test(message)) return '登录失败'
    return '登录失效'
  }
  if (code === 403 || httpStatus === 403) return '无权限'
  if (code === 404 || httpStatus === 404) return '资源不存在'
  if (code >= 500 || httpStatus >= 500) return '系统错误'
  return '操作失败'
}

function normalizeApiError(message, code, httpStatus, raw, networkError) {
  message = sanitizeErrorMessage(message)

  if (networkError && networkError.code === 'ECONNABORTED') {
    message = DEFAULT_MESSAGES.timeout
  } else if (!httpStatus && !message) {
    message = DEFAULT_MESSAGES.network
  }

  if (!message && httpStatus && DEFAULT_MESSAGES[httpStatus]) {
    message = DEFAULT_MESSAGES[httpStatus]
  }

  if (!message) {
    message = DEFAULT_MESSAGES[500]
  }

  // Dubbo 堆栈被误标为 500 时，根据文案修正业务码
  if (code >= 500 && /密码|用户名|账号/.test(message)) {
    code = 401
  } else if (code >= 500 && /无权限/.test(message)) {
    code = 403
  } else if (code >= 500 && /不存在/.test(message)) {
    code = 404
  } else if (code >= 500 && message && message.length < 80) {
    code = 400
  }

  return new ApiError(message, code, httpStatus, raw)
}

export function parseApiError(error) {
  if (error instanceof ApiError) {
    return normalizeApiError(error.message, error.code, error.httpStatus, error.raw)
  }

  const response = error && error.response
  const httpStatus = response && response.status
  const data = response && response.data

  let code = httpStatus || -1
  let message = ''

  if (data && typeof data === 'object') {
    if (data.code != null) code = data.code
    if (data.message) message = data.message
  }

  if (!message && error && error.message) {
    message = error.message
  }

  return normalizeApiError(message, code, httpStatus, error, error)
}

export function isAdminApiUrl(url) {
  if (!url) return false
  const path = url.startsWith('http') ? new URL(url).pathname : url
  return path.startsWith('/admin') || path.startsWith('/api/admin')
}
